import { defineComponent as _defineComponent } from 'vue'
import TradeDetailsForm from './TradeDetailsForm.vue';
import { TimeFrameDate, DrawdownDate } from '../../models/date';
import { TradeDetails } from '../../models/trade';
import UpdatedDate from 'ah-common-lib/src/common/components/time/UpdatedDate.vue';
import { Beneficiary, HedgingInstruments, NonTradeableDays, CutoffTimeModel, FxOperation } from 'ah-api-gateways';
import { makeFormModel } from 'ah-common-lib/src/form/helpers';
import { radioField } from 'ah-common-lib/src/form/models';
import SpotDateForm from './SpotDateForm.vue';
import ForwardDateForm from './ForwardDateForm.vue';
import ForwardOptionsForm from './ForwardOptionsForm.vue';
import { QuotePriceRequest } from '../../requests/pricingRequest';
import { TimeFrames } from 'ah-common-lib/src/constants/timeframes';
import { addMinutes, format, addDays, differenceInDays, startOfDay, startOfToday } from 'date-fns';
import { combineLatest } from 'rxjs';
import { SECOND } from 'ah-common-lib/src/constants/time';
import { ApiError } from 'ah-requests';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue';
import { FormDefinition } from 'ah-common-lib/src/form/interfaces';
import { useAhTradesState } from '../..';

import { useOnBehalfOf } from 'ah-common-lib/src/onBehalfOf/useInjectedOBO';


export default /*#__PURE__*/_defineComponent({
  __name: 'TradeDefinitionFormGroup',
  props: {
    tradeDetails: { default: null },
    tradePrice: { default: null },
    beneficiary: null,
    allowedBuyCurrencies: null,
    allowedSellCurrencies: null,
    forceImmediateSpot: { type: [Boolean, String], default: false },
    syncTradeFunds: { type: [Boolean, String], default: false },
    forceKeep: { type: Boolean, default: false },
    verifyLimits: { type: [String, Boolean], default: false }
  },
  emits: ["update:loadingPrices", "update:tradePrice", "update:tradeDetails", "update:walletBalanceUsageRequired"],
  setup(__props: any, { expose, emit }: { emit: ({
  (e: 'update:loadingPrices', value: boolean): void;
  (e: 'update:tradePrice', value: QuotePriceRequest): void;
  (e: 'update:tradeDetails', value: TradeDetails): void;
  (e: 'update:walletBalanceUsageRequired', value: boolean): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { tradeDetails: TradeDetails | null, tradePrice: QuotePriceRequest | null, beneficiary?: Beneficiary, allowedBuyCurrencies?: string[], allowedSellCurrencies?: string[], forceImmediateSpot: boolean | string, syncTradeFunds: boolean | string, forceKeep: boolean, verifyLimits: string | boolean };



const onBehalfOfClient = useOnBehalfOf();

const tradeState = useAhTradesState();

const tradeDetailsForm = ref<InstanceType<typeof TradeDetailsForm> | null>(null);
const spotDateForm = ref<InstanceType<typeof SpotDateForm> | null>(null);
const forwardDateForm = ref<InstanceType<typeof ForwardDateForm> | null>(null);
const forwardOptionsForm = ref<InstanceType<typeof ForwardOptionsForm> | null>(null);

const requestManager = useRequestManager({
  exposeToParent: ['loadAndSetUpDateForImmediateSpot'],
  onRetryFromParentManager: (k: string) => {
    if (k === 'loadAndSetUpDateForImmediateSpot') {
      loadAndSetUpDateForImmediateSpot();
    }
  },
});

const tradeTypeForm = reactive<FormDefinition>({
  form: makeFormModel({
    name: 'tradeTypeForm',
    fieldType: 'form',
    fields: [
      radioField(
        'type',
        '',
        [
          {
            label: 'Spot',
            value: 'FX_SPOT',
          },
          {
            label: 'Forward',
            value: 'FX_FORWARD',
          },
        ],
        {
          inline: true,
          fieldWrapperClass: 'col col-12 mb-0',
        }
      ),
    ],
  }),
  validation: null,
});



const state = reactive<{
  loadingPrices: boolean;
  timeFrame: TimeFrameDate | null;
  drawdownDate: DrawdownDate | null;
  apiError: ApiError | null;
}>({
  drawdownDate: null,
  timeFrame: null,
  loadingPrices: false,
  apiError: null,
});

const dateSetupTimeout = ref<number | null>(null);

const tradeDirection = computed(() => `${props.tradeDetails?.sellCurrency}${props.tradeDetails?.buyCurrency}`);

const isImmediateSpot = computed(() => props.beneficiary || props.forceImmediateSpot !== false);

const immediateSpotDate = computed(() => {
  if (isImmediateSpot.value && state.timeFrame?.targetDate) {
    const date = new Date(state.timeFrame!.targetDate);
    const difference = differenceInDays(startOfDay(date), startOfToday());

    if (difference === 0) {
      return 'today';
    } else if (difference === 1) {
      return 'tomorrow';
    }
    return `on ${format(date, 'MMM dd, yyyy')}`;
  }
  return '';
});

const allowTradeTypeSelection = computed(() => !isImmediateSpot.value && state.apiError === null);

const clientId = computed(() => {
  return onBehalfOfClient.value?.id ?? tradeState.store.useAuthStore().loggedInIdentity?.client?.id;
});

/**
 * Public methods
 */

function refreshPrices() {
  tradeDetailsForm.value?.refreshData();
}

function touchForms() {
  [tradeDetailsForm.value, spotDateForm.value, forwardDateForm.value, forwardOptionsForm.value].forEach((k) => {
    if (k && typeof k.touchForms === 'function') {
      k.touchForms();
    }
  });
}

function areDatesValid() {
  if (tradeTypeForm.form.type === 'FX_SPOT' && spotDateForm.value) {
    return spotDateForm.value.isValid();
  } else if (tradeTypeForm.form.type === 'FX_FORWARD' && forwardDateForm.value && forwardOptionsForm.value) {
    return forwardDateForm.value.isValid() && forwardOptionsForm.value.isValid();
  }
  return true;
}

onBeforeUnmount(() => {
  if (dateSetupTimeout.value) {
    clearTimeout(dateSetupTimeout.value);
  }
});

/**
 * Private methods
 */

watch(
  () => state.loadingPrices,
  () => {
    emit('update:loadingPrices', state.loadingPrices);
  },
  { immediate: true }
);

watch(
  () => props.tradeDetails,
  () => {
    state.apiError = null;
    if (props.tradeDetails?.timeFrame) {
      state.timeFrame = props.tradeDetails.timeFrame;
      tradeTypeForm.form.type = state.timeFrame.isForward ? 'FX_FORWARD' : 'FX_SPOT';
    } else {
      tradeTypeForm.form.type = 'FX_SPOT';
    }
  },
  { immediate: true }
);

watch(
  () => props.tradePrice,
  () => {
    if (props.tradePrice) {
      state.drawdownDate = props.tradePrice.tradeDetails.drawdownDate ?? null;
    }
  },
  { immediate: true }
);

function loadAndSetUpDateForImmediateSpot() {
  state.apiError = null;
  if (tradeDirection.value && isImmediateSpot.value && clientId.value) {
    requestManager.manager
      .sameOrCancelAndNew(
        'loadAndSetUpDateForImmediateSpot',
        combineLatest([
          tradeState.services.pricingEngine.getNonTradeableDays(
            HedgingInstruments.FX_SPOT,
            tradeDirection.value,
            onBehalfOfClient.value?.id
          ),
          tradeState.services.pricingEngine.getCutoffTime(
            tradeDirection.value,
            clientId.value,
            FxOperation.FX_SPOT,
            undefined,
            onBehalfOfClient.value?.id
          ),
        ]),
        tradeDirection.value
      )
      .pipe(
        catchError((error) => {
          if (error.response.status === 400) {
            displayError(error.response.data);
            return of(null);
          } else {
            throw error;
          }
        })
      )
      .subscribe((payload: [NonTradeableDays, CutoffTimeModel] | null) => {
        if (!payload) return;
        const nonTradeableDays = payload[0];
        const cuttoffTime = payload[1];
        const disallowedDates = [...nonTradeableDays.holidays, ...nonTradeableDays.weekend];
        let targetDate = addMinutes(new Date(cuttoffTime.firstConversionCutoffDatetime), -5);
        while (disallowedDates.includes(format(targetDate, 'dd-MM-yyyy'))) {
          targetDate = addDays(targetDate, 1);
        }

        const timeoutVal = new Date(cuttoffTime.firstConversionCutoffDatetime).valueOf() - Date.now() - 1 * SECOND;
        if (timeoutVal > 0) {
          dateSetupTimeout.value = window.setTimeout(() => loadAndSetUpDateForImmediateSpot(), timeoutVal);
        }
        state.timeFrame = {
          isForward: false,
          targetTimeFrame: TimeFrames.OTHER,
          targetDate: targetDate.toISOString(),
        };
      });
  }
}

watch(() => [tradeDirection.value, isImmediateSpot.value], loadAndSetUpDateForImmediateSpot, { immediate: true });

function displayError(error: ApiError) {
  state.apiError = error;
  tradeDetailsForm.value?.checkPricingEngineValidation(error);
}

expose({ refreshPrices, touchForms, areDatesValid });

return { __sfc: true,props, onBehalfOfClient, tradeState, tradeDetailsForm, spotDateForm, forwardDateForm, forwardOptionsForm, requestManager, tradeTypeForm, emit, state, dateSetupTimeout, tradeDirection, isImmediateSpot, immediateSpotDate, allowTradeTypeSelection, clientId, refreshPrices, touchForms, areDatesValid, loadAndSetUpDateForImmediateSpot, displayError, TradeDetailsForm, UpdatedDate, SpotDateForm, ForwardDateForm, ForwardOptionsForm }
}

})